import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useMother } from '../lib/MotherContext';
import { loginMother } from '../lib/api';
import { toast } from 'sonner';
import { LogIn, KeyRound, Calendar } from 'lucide-react';

const MODE_PIN = 'pin';
const MODE_DOB = 'dob';

export default function ProfileLogin() {
  const { lang, t } = useLanguage();
  const { login } = useMother();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/my';

  const [mode, setMode] = useState(MODE_PIN);
  const [form, setForm] = useState({ phone: '', baby_birth_date: '', pin: '' });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { phone: form.phone.trim() };
      if (mode === MODE_PIN) {
        payload.pin = form.pin;
      } else {
        payload.baby_birth_date = form.baby_birth_date;
      }
      const res = await loginMother(payload);
      login(res.token, res.mother);
      toast.success(lang === 'zh' ? '欢迎回来' : 'Welcome back');
      navigate(next, { replace: true });
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : (lang === 'zh' ? '登入失败' : 'Login failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-app py-8 sm:py-12 max-w-lg animate-fade-in-up" data-testid="profile-login-page">
      <div className="card-base p-6 sm:p-10">
        <div className="w-12 h-12 rounded-2xl bg-sage-light flex items-center justify-center mb-4">
          <LogIn size={20} className="text-sage" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-2">
          {t('profile_login_title')}
        </h1>
        <p className="text-sm text-ink-secondary mb-5 leading-relaxed">
          {lang === 'zh'
            ? '如果你有设置 PIN，只需要输入 WhatsApp 电话和 PIN 就可以登入。'
            : 'If you have set a PIN, you only need your WhatsApp phone number and PIN to log in.'}
        </p>

        {/* Mode tabs */}
        <div className="grid grid-cols-2 gap-2 mb-5 p-1 bg-subtle/60 rounded-2xl" data-testid="login-mode-tabs">
          <button
            type="button"
            onClick={() => setMode(MODE_PIN)}
            data-testid="login-mode-pin"
            className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${
              mode === MODE_PIN ? 'bg-paper text-ink shadow-sm' : 'text-ink-tertiary hover:text-ink'
            }`}
          >
            <KeyRound size={12} />
            {lang === 'zh' ? '使用 PIN 登入' : 'Log in with PIN'}
          </button>
          <button
            type="button"
            onClick={() => setMode(MODE_DOB)}
            data-testid="login-mode-dob"
            className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${
              mode === MODE_DOB ? 'bg-paper text-ink shadow-sm' : 'text-ink-tertiary hover:text-ink'
            }`}
          >
            <Calendar size={12} />
            {lang === 'zh' ? '没有 PIN？用宝宝生日' : 'No PIN? Use baby DOB'}
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('profile_phone')}</span>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              placeholder={t('profile_phone_hint')}
              required
              data-testid="login-phone-input"
              className="input-base"
            />
          </label>

          {mode === MODE_PIN ? (
            <label className="block">
              <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('profile_pin')}</span>
              <input
                type="password"
                inputMode="numeric"
                value={form.pin}
                onChange={(e) => update('pin', e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                data-testid="login-pin-input"
                className="input-base"
              />
            </label>
          ) : (
            <label className="block">
              <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('profile_baby_dob')}</span>
              <input
                type="date"
                value={form.baby_birth_date}
                onChange={(e) => update('baby_birth_date', e.target.value)}
                required
                data-testid="login-dob-input"
                className="input-base"
              />
            </label>
          )}

          <button
            type="submit"
            disabled={saving}
            data-testid="profile-login-btn"
            className="btn-primary w-full disabled:opacity-60"
          >
            {saving ? '…' : t('profile_login_btn')}
          </button>
        </form>

        <div className="mt-5 text-center">
          <Link
            to="/profile/forgot-pin"
            data-testid="profile-forgot-pin-link"
            className="text-sm text-terracotta hover:underline"
          >
            {lang === 'zh' ? '忘记 PIN？' : 'Forgot PIN?'}
          </Link>
        </div>

        <div className="mt-6 text-center text-sm text-ink-secondary">
          {t('profile_no_account')}{' '}
          <Link
            to={`/profile/start${next !== '/my' ? `?next=${encodeURIComponent(next)}` : ''}`}
            className="text-terracotta font-medium hover:underline"
            data-testid="profile-go-register"
          >
            {t('profile_register_link')}
          </Link>
        </div>
      </div>
    </div>
  );
}
